<template>
  <div class="mt-6">
    <Pagination
      v-model="paginationPage"
      :per-page="hitsPerPage || 0"
      :records="nbHits || 0"
      :options="{
        template: shallowRef(CustomPagination),
        edgeNavigation: false,
        chunksNavigation: 'scroll',
        chunk: 3,
        hideCount: true,
      }"
      @paginate="paginate"
    ></Pagination>
  </div>
</template>

<script lang="ts" setup>
import Pagination from 'v-pagination-3'
import { useRouteQuery } from '@vueuse/router'
import CustomPagination from '../common/CustomPagination.vue'

const {
  hitsPerPage = undefined,
  nbHits = undefined,
  nbPages = undefined,
  currentPage = undefined,
} = defineProps<{
  hitsPerPage?: number
  nbHits?: number
  nbPages?: number
  currentPage?: number
}>()

const emit = defineEmits(['updated'])

const pageQuery = useRouteQuery<string | string[] | null>('page')

function normalizedQueryPage(): number {
  const raw = pageQuery.value
  const s = Array.isArray(raw) ? raw[0] : raw
  if (s == null || s === '') return NaN
  const n = Number(s)
  return Number.isFinite(n) ? n : NaN
}

function resolveSyncedPage(p: number | undefined): number {
  const q = normalizedQueryPage()
  if (p != null && p > 0) return p
  if (!Number.isNaN(q) && q > 0) return q
  return 1
}

function clampToTotalPages(p: number, total: number): number {
  if (total <= 0) return Math.max(1, p)
  return Math.min(Math.max(1, p), total)
}

const totalPages = computed(() => {
  if (nbPages != null && nbPages > 0) return nbPages
  if (hitsPerPage != null && hitsPerPage > 0 && nbHits != null && nbHits >= 0) {
    return Math.max(1, Math.ceil(nbHits / hitsPerPage))
  }
  return 0
})

const page = ref(resolveSyncedPage(currentPage))

/** Clamped page for v-pagination — avoids Invalid array length when URL page > totalPages */
const paginationPage = computed({
  get: () => clampToTotalPages(page.value, totalPages.value),
  set: (value: number) => {
    page.value = value
  },
})

const isUpdatingProgrammatically = ref(false)

watch(
  () => [currentPage, pageQuery.value] as const,
  ([p]) => {
    isUpdatingProgrammatically.value = true
    page.value = resolveSyncedPage(p)
    nextTick(() => {
      isUpdatingProgrammatically.value = false
    })
  }
)

watch(
  () => [totalPages.value, page.value] as const,
  ([total, p]) => {
    if (total <= 0 || isUpdatingProgrammatically.value) return
    const clamped = clampToTotalPages(p, total)
    if (clamped === p) return
    isUpdatingProgrammatically.value = true
    page.value = clamped
    nextTick(() => {
      isUpdatingProgrammatically.value = false
      emit('updated', clamped)
    })
  },
  { immediate: true }
)

function paginate(page: number) {
  // Only emit if not updating programmatically
  if (!isUpdatingProgrammatically.value) {
    emit('updated', page)
  }
}
</script>
<style scoped></style>
